import { CSSObject } from '@emotion/react'
import { Color, GemColors, Size, Spacing, Theme, ZIndex } from '@opallabs/gem'

const rootClassName = 'lapidary-tabs'
export const selectors = {
  root: rootClassName,
  list: `${rootClassName}-list`,
  trigger: `${rootClassName}-trigger`,
} as const

export type Variant = 'tertiary' | 'ghost'

const listVariantStyles = {
  ghost: (): CSSObject => ({
    columnGap: Spacing.XS,
  }),
  tertiary: (theme: Theme): CSSObject => ({
    background: Color[theme].Surface.N000,
    border: '1px solid',
    borderColor: Color[theme].Surface.N300,
  }),
}

const triggerVariantStyles = {
  ghost: (theme: Theme): CSSObject => ({
    borderRadius: Size.Rounding * 2,
    color: Color[theme]['Text/Secondary'],

    span: {
      zIndex: ZIndex.Element.Level1,
    },

    '&:hover:enabled': {
      color: Color[theme]['Text/Secondary'],
      '&:before': {
        backgroundColor: GemColors.N100,
      },
    },
    '&[data-state=active]': {
      '&:before': {
        backgroundColor: Color[theme]['Color/Primary/Primary-4'],
      },

      '&:hover:before': {
        backgroundColor: Color[theme]['Color/Primary/Primary-5'],
      },

      span: {
        color: Color[theme].Text.Accent,
      },
    },
  }),
  tertiary: (theme: Theme): CSSObject => ({
    color: Color[theme].Text.Gray,

    ['&:hover']: {
      backgroundColor: Color[theme].Translucent.A08,
    },
    '&[data-state=active]': {
      '&:before': {
        backgroundColor: Color[theme]['Color/Primary/Primary-3'],
      },
      span: {
        color: Color[theme].Text.Accent,
      },
    },
  }),
}

export const list = ({
  theme,
  variant,
}: {
  theme: Theme
  variant: Variant
}): CSSObject => ({
  display: 'flex',
  borderRadius: Size.Rounding,
  width: 'fit-content',
  ...listVariantStyles[variant](theme),

  button: {
    ...triggerVariantStyles[variant](theme),
  },
})

export const trigger: CSSObject = {
  height: 30,
  span: {
    columnGap: Spacing.XS,
  },
  // Tabs sit edge to edge, so a ring on one runs under the next, which is
  // painted after it. The focused tab comes to the front for its ring.
  '&:focus-visible': {
    zIndex: ZIndex.Element.Level2,
  },
  '&[data-state=active]': {
    span: {
      zIndex: ZIndex.Element.Level1,
    },
  },
  ['[data-size=small] &']: {
    height: 22,
  },
}
